import { atom, DefaultValue, useRecoilCallback } from 'recoil';
import type { AtomEffect, RecoilState } from 'recoil';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { broadcastGenerationCommit } from 'modules/viewer/workers/workerSelfRetirement';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { adoptGeneration, proposeBump, type GenerationState } from './policy';

// VX-4302: announce a committed bump to the live workers so the superseded generation self-retires
// (the no-zombie guarantee). Fires on any local generation increase — including a cross-tab
// adoption — which is harmless: the worker closes on the first higher-generation message, so
// multiple tabs announcing the same commit is idempotent. Uses a dedicated native channel, distinct
// from this atom's own sync channel, so there is no echo loop.
const retireSupersededWorkersEffect: AtomEffect<GenerationState> = ({ onSet }) => {
  onSet((newValue, oldValue) => {
    const previousGeneration =
      oldValue instanceof DefaultValue || oldValue == null ? 0 : oldValue.generation;
    if (newValue.generation > previousGeneration) {
      broadcastGenerationCommit(newValue.generation);
    }
  });
};

// VX-4302: Recoil state for the worker-generation escape hatch.

export const DEFAULT_GENERATION_STATE: GenerationState = {
  generation: 0,
  lastBumpAt: 0,
  backoffLevel: 0,
};

// VX-4302: persist ONLY the generation across sessions. backoffLevel/lastBumpAt are session-scoped
// escalation state; persisting them would make a fresh session inherit the previous session's
// escalation — the degraded banner could show on the first stall and the cooldown-free first bump
// would be skipped. A new session restores { generation, backoffLevel: 0, lastBumpAt: 0 }; the full
// state is still coordinated across live tabs by broadcastChannelSynchronizerEffect.
export const GENERATION_STORAGE_KEY = 'viewer.escapeHatch.generation';

// VX-4363: non-Recoil reader for by-name control-message callers that must connect to the live
// generation but cannot go through the atom — either because they render without a RecoilRoot
// (ResetCachePage) or because they are plain classes with no React tree (Ward's WorkerListener).
// Mirrors persistGenerationOnlyEffect's parsing so a cold read agrees with the atom's own hydration.
//
// Takes the caller's own FF.VIEWER_SW_GENERATION_BUMP read as a parameter (rather than reading the
// flag itself) and forces 0 when it's false, mirroring selectGeneration's contract. This matters
// beyond the "flag never turned on" case the ticket originally called out: if the flag was ON long
// enough for a bump to persist and is later turned back off (kill-switch/rollback), the live worker
// (via selectGeneration in useOpenPixelDataSharedWorker) reconnects at generation 0, but a stale
// generation would otherwise still sit in localStorage. Without this gate these callers would target
// a dead worker name instead of the actual live gen-0 one — reintroducing the exact bug this ticket
// fixes, just via a flag-rollback trigger instead of a flag-rollout one.
export function readPersistedGeneration(generationBumpEnabled: boolean): number {
  if (!generationBumpEnabled) return 0;
  try {
    const stored = localStorage.getItem(GENERATION_STORAGE_KEY);
    const generation = stored == null ? 0 : Number.parseInt(stored, 10);
    return Number.isFinite(generation) && generation > 0 ? generation : 0;
  } catch {
    return 0;
  }
}

// VX-4363: collapses the "read FF.VIEWER_SW_GENERATION_BUMP, then readPersistedGeneration" pair that
// was otherwise duplicated at every by-name control-message call site (ResetCachePage, RefreshButton,
// useAuth logout, useClearStoredDataOnClose, Ward's ActionButtons) into one hook.
export function useLiveWorkerGeneration(): number {
  const [generationBumpEnabled] = useFeatureFlagEnabled(FF.VIEWER_SW_GENERATION_BUMP);
  return readPersistedGeneration(generationBumpEnabled);
}

const persistGenerationOnlyEffect: AtomEffect<GenerationState> = ({ setSelf, onSet }) => {
  try {
    const stored = localStorage.getItem(GENERATION_STORAGE_KEY);
    const generation = stored == null ? 0 : Number.parseInt(stored, 10);
    if (Number.isFinite(generation) && generation > 0) {
      setSelf({ ...DEFAULT_GENERATION_STATE, generation });
    }
  } catch {
    // localStorage unavailable (privacy mode / disabled) — keep the default generation 0.
  }

  onSet((newValue, oldValue) => {
    try {
      if (newValue instanceof DefaultValue) {
        localStorage.removeItem(GENERATION_STORAGE_KEY);
        return;
      }
      const previousGeneration =
        oldValue instanceof DefaultValue || oldValue == null ? undefined : oldValue.generation;
      if (newValue.generation !== previousGeneration) {
        localStorage.setItem(GENERATION_STORAGE_KEY, String(newValue.generation));
      }
    } catch {
      // ignore write failures
    }
  });
};

// Durable generation + cross-tab. The generation persists across reloads/sessions so a returning
// window connects to the live worker; the escalation (backoffLevel/lastBumpAt) is session-scoped and
// deliberately NOT persisted (see persistGenerationOnlyEffect). broadcastChannelSynchronizerEffect
// syncs the full state across live tabs; the mergeFn keeps cross-tab convergence idempotent and
// monotonic — two tabs proposing the same next generation converge there, and a stale lower
// generation is ignored.
export const generationStateAtom: RecoilState<GenerationState> = atom<GenerationState>({
  key: 'viewer.escapeHatch.generationState',
  default: DEFAULT_GENERATION_STATE,
  effects: [
    broadcastChannelSynchronizerEffect<GenerationState>({
      mergeFn: (oldValue, newValue) =>
        oldValue == null ? newValue : adoptGeneration(oldValue, newValue),
    }),
    persistGenerationOnlyEffect,
    retireSupersededWorkersEffect,
  ],
});

// VX-4302 #8: per-window UI signal — true once bumps have escalated to the backoff ceiling and
// delivery is still down. Local (not synced or persisted); drives the ambient degraded indicator,
// and clears the moment a frame is delivered.
export const escapeHatchDegradedAtom: RecoilState<boolean> = atom<boolean>({
  key: 'viewer.escapeHatch.degraded',
  default: false,
});

// VX-4302: the manual override behind the "Restart imaging delivery" affordance. Commits a bump
// directly, bypassing quorum and cooldown — the human's strongest recovery, deliberately
// independent of the automated ladder so it still works if that logic is ever wrong.
export function useForceWorkerGenerationBump(): () => void {
  // useRecoilCallback reads the current generation from the snapshot at call time, so there is no
  // need to subscribe to or mirror the atom in a ref.
  return useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        const current = snapshot.getLoadable(generationStateAtom).getValue();
        const next = proposeBump(current, Date.now());
        set(generationStateAtom, next);
        analytics.track(viewer.sys.escapeHatchBump, {
          reason: 'manual',
          generation: next.generation,
        });
      },
    []
  );
}
